/** Planejado: o que o motor vai lançar sozinho e o que precisa de um toque seu.
 * A fila do que precisa de você vem primeiro; as regras vêm escritas em palavras.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useToast } from "../components/Toast";
import { Empty, ErrorBanner, Face, Field, Key, Label, Skeleton, Stamp } from "../components/ui";
import { Icon } from "../components/Icon";
import { dayMonth, dayOnly, daysInMonth, hojeLocal, money, parseMoney, weekday } from "../lib/format";
import {
  useDeletePlan,
  useDeleteRecurrence,
  usePlans,
  useRecurrences,
  useSettingsQuery,
  useUpdateRecurrence,
  useUpdateSettings,
} from "../lib/queries";
import type { Recurrence } from "../lib/types";

const WEEKDAYS = ["segunda", "terça", "quarta", "quinta", "sexta", "sábado", "domingo"];

/** A tecla do sistema mede 32px; no dedo o alvo mínimo é 44. A linha tem 48px de altura,
 *  então a tecla cresce sem empurrar nada. */
const ALVO = { minHeight: 44, width: 44 };

/** A regra em palavras, como a pessoa a diria. */
export function regraEmPalavras(r: Recurrence): string {
  const cada = r.interval > 1 ? `a cada ${r.interval} ` : "";
  if (r.frequency === "daily") return `${cada || "todo "}dia`;
  if (r.frequency === "weekly")
    return `${cada ? `${cada}semanas` : "toda semana"}, ${WEEKDAYS[r.weekday ?? 0]}`;
  const dia = {
    fixed_day: `dia ${r.day}`,
    last_day: "último dia do mês",
    nth_business_day: `${r.nth}º dia útil`,
    last_business_day: "último dia útil",
    nth_weekday: `${r.nth}ª ${WEEKDAYS[r.weekday ?? 0]}`,
    last_weekday: `última ${WEEKDAYS[r.weekday ?? 0]}`,
  }[r.due_rule];
  const freq = r.frequency === "yearly" ? "todo ano" : cada ? `${cada}meses` : "todo mês";
  const ajuste = r.adjust_business_day ? " · adia para o próximo dia útil" : "";
  return `${freq}, ${dia}${ajuste}`;
}

export function Planejado() {
  const recs = useRecurrences();
  const plans = usePlans();
  const [acao, setAcao] = useState<Alvo | null>(null);

  if (recs.isPending || plans.isPending)
    return <section className="view on"><Skeleton lines={6} /></section>;
  if (recs.error)
    return (
      <section className="view on">
        <ErrorBanner message={(recs.error as Error).message} onRetry={() => recs.refetch()} />
      </section>
    );

  const ativas = (recs.data ?? []).filter((r) => r.active);
  // Pausadas tinham virado um beco: a lista só mostrava `r.active`, o servidor continuava
  // devolvendo a regra pausada e não havia como retomar nem excluir. O dinheiro some da
  // tela e continua no banco — a pior combinação possível.
  const pausadas = (recs.data ?? []).filter((r) => !r.active);
  const variaveis = ativas.filter((r) => r.is_variable);

  return (
    <section className="view on" aria-label="Planejado">
      <div className="plan">
        <OsDoisNumeros />

        {variaveis.length > 0 && (
          <Face className="log">
            <div className="sec">
              <Label>Contas variáveis</Label>
              <Label dim>{variaveis.length} estimadas</Label>
            </div>
            {variaveis.map((r) => (
              <div className={`line p-${r.tx_kind === "income" ? "in" : r.tx_kind === "transfer" ? "sv" : "out"}`} key={r.id}>
                <div className="d">
                  {r.next_on ? weekday(r.next_on) : "—"}
                  <b>{r.next_on ? dayOnly(r.next_on) : "—"}</b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{r.name}</span>
                    <Stamp text="Estimado" cls="est" />
                  </div>
                  <small>
                    {regraEmPalavras(r)} ·{" "}
                    {r.estimate_mode === "last"
                      ? "última confirmação"
                      : `média de ${r.estimate_mode === "avg3" ? 3 : 6}`}
                  </small>
                </div>
                <div className="amt">
                  <span>−{money(r.amount)}</span>
                  <small>confirmar em Hoje</small>
                </div>
              </div>
            ))}
          </Face>
        )}

        <Face className="log">
          <div className="sec">
            <Label>Recorrências</Label>
            <Label dim>{ativas.length} ativas</Label>
          </div>
          {ativas.length === 0 && (
            <Empty title="Nenhuma recorrência">
              Cadastre o salário, o aluguel e as contas fixas: elas aparecem sozinhas em todo mês.
            </Empty>
          )}
          {ativas.map((r) => (
            <div className={`line p-${r.tx_kind === "income" ? "in" : r.tx_kind === "transfer" ? "sv" : "out"}`} key={r.id}>
              <div className="d">
                {r.next_on ? "próx." : ""}
                <b>{r.next_on ? dayOnly(r.next_on) : "—"}</b>
              </div>
              <div className="what">
                <div className="t">
                  <span className="desc">{r.name}</span>
                  <span className={`kind ${r.tx_kind === "income" ? "in" : r.tx_kind === "transfer" ? "sv" : "out"}`}>
                    {r.tx_kind === "income" ? "Entrada" : r.tx_kind === "transfer" ? "Economia" : "Saída"}
                  </span>
                </div>
                <small>
                  {regraEmPalavras(r)} · {r.account_name}
                  {r.next_on ? ` · próxima ${dayMonth(r.next_on)}` : ""}
                </small>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className={`amt ${r.tx_kind === "income" ? "t-in" : ""}`}>
                  {r.tx_kind === "income" ? "+" : "−"}
                  {money(r.amount)}
                </span>
                <Key
                  icon="pen"
                  style={ALVO}
                  title={`Ações de ${r.name}`}
                  onClick={() => setAcao({ tipo: "rec", id: r.id, nome: r.name, ativa: true })}
                />
              </div>
            </div>
          ))}
          <Link className="add" to="/planejado/nova-recorrencia">
            <Icon name="plus" />
            Nova recorrência
          </Link>
        </Face>

        {pausadas.length > 0 && (
          <Face className="log">
            <div className="sec">
              <Label>Pausadas</Label>
              <Label dim>{pausadas.length} fora da projeção</Label>
            </div>
            {pausadas.map((r) => (
              <div className="line" key={r.id}>
                <div className="d">
                  <b>—</b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{r.name}</span>
                    <Stamp text="Pausada" cls="plan" />
                  </div>
                  <small>
                    {regraEmPalavras(r)} · {r.account_name} · não gera nada enquanto estiver assim
                  </small>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className="amt">
                    {r.tx_kind === "income" ? "+" : "−"}
                    {money(r.amount)}
                  </span>
                  <Key
                    icon="pen"
                    style={ALVO}
                    title={`Ações de ${r.name}`}
                    onClick={() => setAcao({ tipo: "rec", id: r.id, nome: r.name, ativa: false })}
                  />
                </div>
              </div>
            ))}
            <div className="rule" style={{ padding: "8px 14px 12px" }}>
              Retomar devolve a regra à projeção a partir de hoje; as ocorrências do período
              pausado não são recriadas.
            </div>
          </Face>
        )}

        <Face className="log">
          <div className="sec">
            <Label>Parcelas</Label>
            <Label dim>{(plans.data ?? []).length} planos</Label>
          </div>
          {(plans.data ?? []).length === 0 && (
            <Empty title="Nenhum parcelamento">
              Compra parcelada no cartão ou crediário na loja entram aqui, com fim à vista.
            </Empty>
          )}
          {(plans.data ?? []).map((p) => (
            <div className="line p-out" key={p.id}>
              <div className="d">
                {p.next_on ? "próx." : ""}
                <b>{p.next_on ? dayOnly(p.next_on) : "—"}</b>
              </div>
              <div className="what">
                <div className="t">
                  <span className="desc">{p.description}</span>
                </div>
                <small>
                  {(p.paid_count ?? 0)} de {p.installment_count} pagas · {p.account_name}
                  {p.payee ? ` · ${p.payee}` : ""}
                </small>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className="amt">
                  −{money(p.total_amount / p.installment_count)}
                  <small>restam {money(p.remaining ?? 0)}</small>
                </span>
                <Key
                  icon="pen"
                  style={ALVO}
                  title={`Ações de ${p.description}`}
                  onClick={() => setAcao({ tipo: "plan", id: p.id, nome: p.description })}
                />
              </div>
            </div>
          ))}
          <Link className="add" to="/planejado/novo-parcelamento">
            <Icon name="plus" />
            Novo parcelamento
          </Link>
        </Face>
      </div>

      {acao && <Acoes alvo={acao} onClose={() => setAcao(null)} />}
    </section>
  );
}

type Alvo = { tipo: "rec" | "plan"; id: string; nome: string; ativa?: boolean };

/** Editar, pausar/retomar e excluir.
 *
 * A folha nasceu só com "Pausar · Fechar · Excluir" — e a tecla que a abre é um LÁPIS.
 * Prometia edição e não entregava: "o aluguel subiu" custava apagar a regra e recriá-la,
 * perdendo o histórico dela. Agora a primeira ação é editar, no mesmo formulário que cria.
 *
 * Pausar também não tinha volta: a regra sumia da lista (que filtrava `r.active`) e o
 * servidor continuava devolvendo-a. Retomar mora aqui, ao lado de pausar, porque é a mesma
 * decisão vista dos dois lados.
 */
function Acoes({ alvo, onClose }: { alvo: Alvo; onClose: () => void }) {
  const nav = useNavigate();
  const mudar = useUpdateRecurrence();
  const apagarRec = useDeleteRecurrence();
  const apagarPlano = useDeletePlan();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const aviso = useToast();
  const ocupado = mudar.isPending || apagarRec.isPending || apagarPlano.isPending;
  const pausada = alvo.tipo === "rec" && alvo.ativa === false;

  const fazer = async (fn: () => Promise<unknown>, feito: string) => {
    setErro(null);
    try {
      await fn();
      aviso.ok(feito);
      onClose();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  // Fechar ANTES de navegar: as duas rotas montam o mesmo `<Planejado/>` na mesma posição
  // da árvore, então o React preserva o estado dela — e esta folha ficaria aberta por cima
  // da folha de edição.
  const editar = () => {
    onClose();
    nav(alvo.tipo === "rec" ? `/planejado/recorrencia/${alvo.id}` : `/planejado/parcelamento/${alvo.id}`);
  };

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-label={`Ações de ${alvo.nome}`}>
      <div className="panel">
        <div className="handle" />
        <h2>
          <span>{alvo.nome}</span>
          <Key icon="x" title="Fechar" onClick={onClose} />
        </h2>

        {alvo.tipo === "rec" ? (
          <div className="rule">
            Editar muda valor, dia, conta e regra das próximas ocorrências.{" "}
            {pausada
              ? "Retomar devolve a regra à projeção a partir de hoje."
              : "Pausar mantém o histórico e para de gerar as próximas."}{" "}
            Excluir tira a regra e as ocorrências que ainda não aconteceram.
          </div>
        ) : (
          <div className="rule">
            Editar muda o total, o número de parcelas e o primeiro vencimento. Excluir o plano tira
            as parcelas que ainda não venceram; as já pagas ficam no histórico.
          </div>
        )}

        <div className="acts">
          {alvo.tipo === "rec" ? (
            <Key
              disabled={ocupado}
              onClick={() =>
                fazer(
                  () => mudar.mutateAsync({ id: alvo.id, active: pausada }),
                  pausada ? `${alvo.nome} voltou para a projeção` : `${alvo.nome} pausada`,
                )
              }
            >
              {pausada ? "Retomar" : "Pausar"}
            </Key>
          ) : (
            <span />
          )}
          <Key primary icon="pen" disabled={ocupado} onClick={editar}>
            Editar
          </Key>
        </div>

        {erro && (
          <div className="banner" role="alert">
            {erro}
          </div>
        )}

        <div className="acts">
          <Key onClick={onClose}>Fechar</Key>
          {!confirmando ? (
            <Key danger disabled={ocupado} onClick={() => setConfirmando(true)}>
              Excluir
            </Key>
          ) : (
            <Key
              danger
              disabled={ocupado}
              onClick={() =>
                fazer(
                  () =>
                    alvo.tipo === "rec" ? apagarRec.mutateAsync(alvo.id) : apagarPlano.mutateAsync(alvo.id),
                  `${alvo.nome} excluída`,
                )
              }
            >
              {ocupado ? "Excluindo…" : "Confirmar exclusão"}
            </Key>
          )}
        </div>
      </div>
    </div>
  );
}


/** O diário do mês e o colchão.
 *
 * Estavam em Ajustes, e o Paulo perguntou por quê — com razão. Não são configuração do
 * app: são duas decisões de dinheiro, do mesmo tipo que cadastrar o aluguel. Mudar o
 * diário muda a projeção de todos os dias, exatamente como mudar uma recorrência. O que
 * fica em Ajustes é o que não move número: tema, senha, categorias, família.
 */
function OsDoisNumeros() {
  const { data } = useSettingsQuery();
  const salvar = useUpdateSettings();
  const aviso = useToast();
  const [diario, setDiario] = useState("");
  const [colchao, setColchao] = useState("");

  useEffect(() => {
    if (!data) return;
    setDiario(String(data.settings.daily_budget ?? 0).replace(".", ","));
    setColchao(String(data.settings.buffer ?? 0).replace(".", ","));
  }, [data]);

  const num = (v: string) => parseMoney(v);
  const mudou =
    !!data &&
    (num(diario) !== Number(data.settings.daily_budget ?? 0) ||
      num(colchao) !== Number(data.settings.buffer ?? 0));
  const porDia = num(diario) > 0 ? num(diario) / daysInMonth(hojeLocal()) : 0;

  return (
    <Face className="log">
      <div className="sec">
        <Label>Os dois números do mês</Label>
        <Label dim>governam a projeção</Label>
      </div>

      <div className="line p-dy">
        <div className="d" />
        <div className="what">
          <div className="t">
            <span className="desc">Diário</span>
          </div>
          <small>
            {porDia > 0
              ? `${money(porDia)} por dia neste mês — o motor dilui e desconta o que você já gastou`
              : "Sem diário, a tela Hoje não mostra a barra do dia"}
          </small>
        </div>
        <div style={{ paddingRight: 14, width: 130 }}>
          <Field label="Por mês" className="amount">
            <input inputMode="decimal" value={diario} onChange={(e) => setDiario(e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="line p-sv">
        <div className="d" />
        <div className="what">
          <div className="t">
            <span className="desc">Colchão</span>
          </div>
          <small>O que você não quer encostar. Descontado antes de o painel dizer que está no azul.</small>
        </div>
        <div style={{ paddingRight: 14, width: 130 }}>
          <Field label="Valor" className="amount">
            <input inputMode="decimal" value={colchao} onChange={(e) => setColchao(e.target.value)} />
          </Field>
        </div>
      </div>

      {mudou && (
        <div className="acts" style={{ padding: "0 14px 12px" }}>
          <Key
            primary
            disabled={salvar.isPending}
            onClick={() =>
              salvar.mutate(
                { daily_budget: num(diario), buffer: num(colchao) },
                {
                  onSuccess: () => aviso.ok("Projeção refeita com os números novos"),
                  onError: (e) => aviso.erro((e as Error).message),
                },
              )
            }
          >
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </Key>
        </div>
      )}
      {salvar.isError && <ErrorBanner message={(salvar.error as Error).message} />}
    </Face>
  );
}
